import React, { useState } from 'react';
import { Save, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { updateItem, TYPE_LABEL } from './activitiesApi';

export const ItemEditorDialog = ({ item, onClose, onSaved }) => {
  const [title, setTitle] = useState(item.title || '');
  const [instructions, setInstructions] = useState(item.instructions || '');
  const [totalMarks, setTotalMarks] = useState(item.total_marks || 20);
  const [questions, setQuestions] = useState(item.questions || []);
  const [saving, setSaving] = useState(false);

  const setQ = (i, patch) => setQuestions((qs) => qs.map((q, j) => (j === i ? { ...q, ...patch } : q)));
  const addQ = () => setQuestions((qs) => [...qs, { q_num: qs.length + 1, type: 'text', text: '', answer: '', guide: '', marks: 3 }]);
  const delQ = (i) => setQuestions((qs) => qs.filter((_, j) => j !== i).map((q, j) => ({ ...q, q_num: j + 1 })));

  const save = async () => {
    setSaving(true);
    try {
      await updateItem(item.id, { title, instructions, total_marks: Number(totalMarks), questions });
      toast.success('Saved');
      onSaved();
      onClose();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="item-editor-dialog">
        <DialogHeader><DialogTitle>Edit {item.code}</DialogTitle></DialogHeader>
        <div className="grid gap-3 md:grid-cols-[1fr_120px]">
          <div><Label>Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="item-editor-title" /></div>
          <div><Label>Total marks</Label><Input type="number" value={totalMarks} onChange={(e) => setTotalMarks(e.target.value)} data-testid="item-editor-total" /></div>
        </div>
        <div><Label>Instructions for students</Label><Textarea rows={2} value={instructions} onChange={(e) => setInstructions(e.target.value)} /></div>
        <div className="space-y-3">
          {questions.map((q, i) => (
            <div key={i} className="rounded-lg border p-3 space-y-2 bg-slate-50" data-testid={`editor-question-${q.q_num}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-sm">Q{q.q_num}</span>
                <Input className="w-36 h-8 text-xs" placeholder="Part (optional)" value={q.part || ''} onChange={(e) => setQ(i, { part: e.target.value || undefined })} />
                <select className="h-8 border rounded-md text-xs px-2" value={q.type} onChange={(e) => setQ(i, { type: e.target.value })}>
                  {Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <Input type="number" className="w-20 h-8 text-xs" value={q.marks} onChange={(e) => setQ(i, { marks: Number(e.target.value) })} />
                <span className="text-xs text-slate-500">marks</span>
                <Button size="icon" variant="ghost" className="ml-auto text-rose-600" onClick={() => delQ(i)}><Trash2 className="h-4 w-4" /></Button>
              </div>
              <Textarea rows={2} value={q.text} onChange={(e) => setQ(i, { text: e.target.value })} placeholder="Question / task" className="text-sm" />
              {(q.type === 'mcq') && <Input value={(q.options || []).join(' | ')} onChange={(e) => setQ(i, { options: e.target.value.split('|').map((s) => s.trim()).filter(Boolean) })} placeholder="Options separated by | (e.g. A Router | B Switch)" className="text-sm" />}
              <Textarea rows={2} value={q.answer || ''} onChange={(e) => setQ(i, { answer: e.target.value })} placeholder="Model answer / expected result (never shown to students)" className="text-sm border-emerald-200" />
              <Input value={q.guide || ''} onChange={(e) => setQ(i, { guide: e.target.value })} placeholder="Marking guide (how to award marks)" className="text-xs" />
            </div>
          ))}
          <Button size="sm" variant="outline" onClick={addQ}><Plus className="h-4 w-4 mr-1" />Add question</Button>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={saving} onClick={save} data-testid="item-editor-save"><Save className="h-4 w-4 mr-1" />Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
